import React, { useMemo, useState } from 'react'
import { Badge, Button, Modal } from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import type { NotificationTemplate } from '@/types'
import { useNotificationTemplate, useNotificationTemplates } from '../hooks/useNotifications'
import { usePermissions } from '@/hooks/usePermissions'
import { EditNotificationTemplateForm } from './EditNotificationTemplateForm'
import { cn } from '@/lib/cn'
import { Pencil } from 'lucide-react'
import { QueryErrorState } from '@/components/common/QueryErrorState'

const cardClasses =
  'overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800'

export function NotificationTemplatesMatrix(): React.ReactElement {
  const { data: templates = [], isLoading, error, refetch } = useNotificationTemplates()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const { data: selectedTemplate, isLoading: detailLoading } = useNotificationTemplate(selectedId)
  const { can } = usePermissions()
  const canEditTemplates = can('settings', 'update')

  const grouped = useMemo(() => {
    const byType = new Map<string, NotificationTemplate[]>()
    for (const t of templates) {
      const key = (t.type ?? 'Other').toUpperCase()
      if (!byType.has(key)) byType.set(key, [])
      byType.get(key)!.push(t)
    }
    return Array.from(byType.entries()).sort(([a], [b]) => a.localeCompare(b))
  }, [templates])

  const handleCloseModal = (): void => {
    setSelectedId(null)
  }

  if (isLoading) {
    return (
      <div className={cardClasses}>
        <SectionLoader variant="table" cols={7} rows={6} label="Loading notification templates" />
      </div>
    )
  }

  if (error) {
    return (
      <div className={cardClasses}>
        <QueryErrorState error={error} title="Couldn't load notification templates" onRetry={refetch} />
      </div>
    )
  }

  if (templates.length === 0) {
    return (
      <div className={cardClasses}>
        <div className="rounded-lg border border-gray-200 bg-white py-12 text-center text-sm text-gray-600 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
          No notification templates.
        </div>
      </div>
    )
  }

  return (
    <>
      <div className={cardClasses}>
        <div className="p-4 sm:p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse text-left text-sm">
              <thead>
                <tr>
                  <th className="w-[min(200px,25%)] border-b border-gray-200 bg-gray-50 px-4 py-3 font-semibold uppercase tracking-wider text-gray-600 dark:border-gray-700 dark:bg-gray-800/80 dark:text-gray-400">
                    Template
                  </th>
                  <th className="border-b border-gray-200 bg-gray-50 px-4 py-3 font-semibold uppercase tracking-wider text-gray-600 dark:border-gray-700 dark:bg-gray-800/80 dark:text-gray-400">
                    Code
                  </th>
                  <th className="border-b border-gray-200 bg-gray-50 px-4 py-3 font-semibold uppercase tracking-wider text-gray-600 dark:border-gray-700 dark:bg-gray-800/80 dark:text-gray-400">
                    Type
                  </th>
                  <th className="border-b border-gray-200 bg-gray-50 px-4 py-3 font-semibold uppercase tracking-wider text-gray-600 dark:border-gray-700 dark:bg-gray-800/80 dark:text-gray-400">
                    In-app
                  </th>
                  <th className="border-b border-gray-200 bg-gray-50 px-4 py-3 font-semibold uppercase tracking-wider text-gray-600 dark:border-gray-700 dark:bg-gray-800/80 dark:text-gray-400">
                    Email
                  </th>
                  <th className="border-b border-gray-200 bg-gray-50 px-4 py-3 font-semibold uppercase tracking-wider text-gray-600 dark:border-gray-700 dark:bg-gray-800/80 dark:text-gray-400">
                    Active
                  </th>
                  <th className="border-b border-gray-200 bg-gray-50 px-4 py-3 text-right font-semibold uppercase tracking-wider text-gray-600 dark:border-gray-700 dark:bg-gray-800/80 dark:text-gray-400">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {grouped.map(([typeLabel, typeTemplates]) => (
                  <React.Fragment key={typeLabel}>
                    <tr className="bg-gray-50 dark:bg-gray-800/50">
                      <td
                        colSpan={7}
                        className="border-b border-gray-200 px-4 py-2 text-xs font-bold uppercase tracking-wider text-gray-600 dark:border-gray-700 dark:text-gray-400"
                      >
                        {typeLabel.replace(/_/g, ' ')}
                      </td>
                    </tr>
                    {typeTemplates.map((t) => (
                      <tr
                        key={t._id}
                        className={cn(
                          'border-b border-gray-100 transition-colors even:bg-gray-50/50 hover:bg-gray-50 dark:border-gray-700 dark:even:bg-gray-800/30 dark:hover:bg-gray-800/50',
                          !t.isActive && 'opacity-70'
                        )}
                      >
                        <td className="border-b border-gray-100 px-4 py-3 dark:border-gray-700">
                          <span className="font-medium text-gray-900 dark:text-gray-100">
                            {t.name}
                          </span>
                        </td>
                        <td className="whitespace-nowrap border-b border-gray-100 px-4 py-3 font-mono text-xs text-gray-600 dark:border-gray-700 dark:text-gray-400">
                          {t.code}
                        </td>
                        <td className="whitespace-nowrap border-b border-gray-100 px-4 py-3 text-gray-600 dark:border-gray-700 dark:text-gray-400">
                          {t.type}
                        </td>
                        <td className="border-b border-gray-100 px-4 py-3 dark:border-gray-700">
                          {t.inApp.enabled ? (
                            <Badge variant="success">On</Badge>
                          ) : (
                            <Badge variant="neutral">Off</Badge>
                          )}
                          <span className="ml-1 block truncate max-w-[120px] text-xs text-gray-500 dark:text-gray-400" title={t.inApp.title}>
                            {t.inApp.title}
                          </span>
                        </td>
                        <td className="border-b border-gray-100 px-4 py-3 dark:border-gray-700">
                          {t.email.enabled ? (
                            <Badge variant="success">On</Badge>
                          ) : (
                            <Badge variant="neutral">Off</Badge>
                          )}
                          {t.email.subject && (
                            <span className="ml-1 block truncate max-w-[120px] text-xs text-gray-500 dark:text-gray-400" title={t.email.subject}>
                              {t.email.subject}
                            </span>
                          )}
                        </td>
                        <td className="border-b border-gray-100 px-4 py-3 dark:border-gray-700">
                          {t.isActive ? (
                            <Badge variant="success">Active</Badge>
                          ) : (
                            <Badge variant="neutral">Inactive</Badge>
                          )}
                        </td>
                        <td className="border-b border-gray-100 py-3 text-right dark:border-gray-700">
                          {canEditTemplates && (
                            <Button
                              type="button"
                              variant="ghost"
                              onClick={() => setSelectedId(t._id)}
                              aria-label={`Edit ${t.name}`}
                            >
                              <Pencil className="h-4 w-4 shrink-0" aria-hidden />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Modal
        open={selectedId !== null}
        onClose={handleCloseModal}
        title={selectedTemplate ? `Edit: ${selectedTemplate.name}` : 'Edit template'}
        className="max-w-lg"
      >
        {detailLoading ? (
          <SectionLoader variant="detail" rows={3} label="Loading notification template" />
        ) : selectedTemplate ? (
          <EditNotificationTemplateForm
            template={selectedTemplate}
            onCancel={handleCloseModal}
            onSuccess={handleCloseModal}
          />
        ) : null}
      </Modal>
    </>
  )
}

NotificationTemplatesMatrix.displayName = 'NotificationTemplatesMatrix'
